import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { convertArrayToCSV } from 'convert-array-to-csv';
import React from 'react';
import { TableData } from '../../design-system';
import { Column } from '../../utils/dataTable/columns';
import { FieldValueGetter, getCsvCellValue, getCsvColumns, TableRow } from '../../utils/dataTable/csvExport';

export interface ExportProps<RowData extends TableData> {
  columns: Column<RowData>[];
  data: TableRow<RowData>[];
  dateFormat?: string;
  exportFileName: string;
  getFieldValue: FieldValueGetter<RowData>;
  icons: { Export: () => React.ReactElement };
  onDownload?: () => void;
}

const downloadFile = (fileName: string, data: string): void => {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const el = document.createElement('a');
  el.href = URL.createObjectURL(blob);
  el.download = `${fileName}.csv`;
  document.body.appendChild(el);
  el.click();
  el.remove();
};

export function ExportCsv<RowData extends TableData>({
  columns,
  data,
  exportFileName,
  getFieldValue,
  icons,
  dateFormat,
  onDownload,
}: ExportProps<RowData>): JSX.Element {
  const exportData = React.useCallback(() => {
    const csvColumns = getCsvColumns(columns);
    const dataArrays = data.map((row) =>
      csvColumns.map((col) =>
        getCsvCellValue({ columnField: col.field, columnType: col.type, dateFormat, value: getFieldValue(row, col) }),
      ),
    );
    downloadFile(exportFileName, convertArrayToCSV(dataArrays, { header: csvColumns.map((col) => String(col.title)) }));
    if (typeof onDownload !== 'undefined') {
      onDownload();
    }
  }, [columns, dateFormat, exportFileName, getFieldValue, data, onDownload]);

  return (
    <Tooltip title="Export visible columns">
      <IconButton color="inherit" onClick={exportData} size="large">
        <icons.Export />
      </IconButton>
    </Tooltip>
  );
}
